import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/lib/base/buttons/button';
import { sanitizeError } from '@/lib/utils/errors';

import { useAuthInfo } from '../../allauth_auth/hooks';
import FormErrors, { hasErrors } from '../../components/FormErrors';
import * as allauth from '../../lib/allauth';
import AuthenticateFlow from './AuthenticateFlow';

interface AuthenticateCodeProps {
  authenticatorType: string;
  children?: React.ReactNode;
}

interface ResponseContent {
  errors?: Array<{ param?: string; message: string }>;
}

export default function AuthenticateCode(props: AuthenticateCodeProps) {
  const [code, setCode] = useState('');
  const [response, setResponse] = useState<{
    fetching: boolean;
    content: ResponseContent | null;
  }>({ fetching: false, content: null });
  const authInfo = useAuthInfo();

  if (authInfo?.pendingFlow?.id !== allauth.Flows.MFA_AUTHENTICATE) {
    return <Navigate to='/' />;
  }

  function submit() {
    setResponse({ ...response, fetching: true });
    allauth
      .mfaAuthenticate(code)
      .then(content => {
        setResponse(r => {
          return { ...r, content };
        });
      })
      .catch(e => {
        console.error(e);
        toast.error(sanitizeError(e));
      })
      .then(() => {
        setResponse(r => {
          return { ...r, fetching: false };
        });
      });
  }
  return (
    <AuthenticateFlow authenticatorType={props.authenticatorType}>
      {props.children}
      <div className='space-y-1.5'>
        <label
          className='block text-sm font-medium text-gray-700 dark:text-gray-200'
          htmlFor='code'
        >
          Authentication code
        </label>
        <input
          type='text'
          id='code'
          className={`w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent transition-colors text-center text-lg font-mono tracking-widest ${hasErrors({ errors: response.content?.errors, param: 'code' }) ? 'border-red-500 focus:ring-red-500' : ''}`}
          placeholder='000000'
          required
          onChange={e => setCode(e.target.value)}
          value={code}
          name='code'
          maxLength={6}
          autoComplete='one-time-code'
        />
        <FormErrors param='code' errors={response.content?.errors} />
        <p className='text-xs text-gray-500 dark:text-gray-400 text-center'>
          Enter the 6-digit code from your authenticator app
        </p>
      </div>
      <div className='mt-6'>
        <Button
          onClick={() => submit()}
          disabled={response.fetching || code.length < 6}
          className='w-full'
          isLoading={response.fetching}
          showTextWhileLoading
        >
          {response.fetching ? 'Verifying…' : 'Verify and sign in'}
        </Button>
      </div>
    </AuthenticateFlow>
  );
}
